import {
  ImportModuleConceptCandidate,
  ImportModuleConceptField,
  ImportModuleConceptRow,
  ImportModuleDecisionKind,
  ImportModuleMatchStatus,
} from '../../../core/definitions/import-module';
import {ConceptOption, conceptOptionAncestors, conceptOptionLabel} from '../concept-picker/concept-option.model';

/** What the run does with a reviewed value, as its decision stands. */
export type RunOutcomeKind = 'link' | 'create' | 'split' | 'skip' | 'undecided';

export interface RunOutcome {
  kind: RunOutcomeKind;
  /** `link`: the term the value is written as. */
  candidate?: ImportModuleConceptCandidate;
  /** `create`: the term the new one goes under, `null` for a root term. */
  parentId?: string | null;
  /** `split`: how many pieces the value splits into. */
  pieceCount?: number;
  /** `split`: pieces whose own row is undecided, so the run skips them. */
  undecidedPieces?: number;
}

/** A `RunOutcome` as the page shows it: a TRANS key line with its
 * parameters, and an optional second line - a TRANS key, or plain text
 * for a term id. */
export interface RunOutcomeView {
  tone: 'ok' | 'warn' | 'neutral';
  titleKey: string;
  titleParams?: Record<string, string | number>;
  detailKey?: string;
  detailParams?: Record<string, string | number>;
  detailText?: string;
}

/** A row filter on the page: every row, the undecided ones, or one
 * `match_status`. */
export type ReviewFilter = 'all' | 'undecided' | ImportModuleMatchStatus;

export type ReviewSort = 'occurrences' | 'value';

export const MATCH_STATUSES: ImportModuleMatchStatus[] = ['ambiguous', 'no_match', 'composite', 'struck_only'];

/** The number a row's status chip shows: the terms an ambiguous value
 * names (not the similar spellings), or a composite value's pieces. */
export function statusCount(row: ImportModuleConceptRow): number {
  if (row.match_status === 'composite') {
    return row.pieces?.length ?? 0;
  }
  return row.candidates.filter(c => !c.candidate_is_suggestion).length;
}

/** The ticked candidate of a row, if any. */
export function pickedCandidate(row: ImportModuleConceptRow): ImportModuleConceptCandidate | null {
  return row.candidates.find(c => c.is_selected) ?? null;
}

/** Whether the operator has made a complete decision: a `pick_existing`
 * needs a ticked candidate and a `create_new` a name. */
export function isRowDecided(row: ImportModuleConceptRow): boolean {
  switch (row.decision_kind) {
    case 'skip':
    case 'split':
      return true;
    case 'pick_existing':
      return !!pickedCandidate(row);
    case 'create_new':
      return !!row.new_concept_name?.trim();
    default:
      return false;
  }
}

/** Whether `row`'s decision has a detail to expand: any decision but
 * `skip`. */
export function hasDetail(row: ImportModuleConceptRow): boolean {
  return !!row.decision_kind && row.decision_kind !== 'skip';
}

/**
 * Drops the term a profile seeded on `row`. The backend's finalize step
 * binds a row that carries a `resolved_artifact_id` to that id, whatever
 * its decision, so every operator edit of a row's decision or pick calls
 * this, or the edit is discarded at run time.
 */
export function clearProfileBinding(row: ImportModuleConceptRow): void {
  row.resolved_artifact_id = null;
}

/** Sets `row`'s decision to `kind`; see `clearProfileBinding`. */
export function setRowDecision(row: ImportModuleConceptRow, kind: ImportModuleDecisionKind): void {
  row.decision_kind = kind;
  clearProfileBinding(row);
}

/** Ticks `candidateId` on `row`; see `clearProfileBinding`. */
export function selectCandidate(row: ImportModuleConceptRow, candidateId: string): void {
  row.candidates.forEach(c => c.is_selected = c.candidate_artifact_id === candidateId);
  clearProfileBinding(row);
}

/** Adds a searched term to `row`'s candidates unless it is already one. */
export function addCandidate(row: ImportModuleConceptRow, option: ConceptOption): void {
  if (!row.candidates.some(c => c.candidate_artifact_id === option.id)) {
    row.candidates.push({
      candidate_artifact_id: option.id,
      candidate_name: conceptOptionLabel(option),
      candidate_code: null,
      candidate_path: option.path ?? '',
      is_selected: false,
    });
  }
}

/**
 * A field's rows by value, and the source cells its split composites add
 * to each piece, so what one row's outcome or count reads from the other
 * rows is a lookup. Built from the rows' decisions as they stand, so it is
 * rebuilt after an edit.
 */
export interface FieldIndex {
  /** The row that decides a value key; a value with no row names a term by
   * itself. */
  rowByKey: Map<string, ImportModuleConceptRow>;
  /** Per piece value key: the summed `occurrence_count` of the rows decided
   * `split` that hold that piece. */
  splitOccurrences: Map<string, number>;
}

export function indexField(field: ImportModuleConceptField): FieldIndex {
  const rowByKey = new Map<string, ImportModuleConceptRow>();
  const splitOccurrences = new Map<string, number>();
  for (const row of field.review_rows) {
    if (!rowByKey.has(row.source_value_key)) {
      rowByKey.set(row.source_value_key, row);
    }
    if (row.decision_kind === 'split') {
      for (const key of new Set((row.pieces ?? []).map(piece => piece.value_key))) {
        splitOccurrences.set(key, (splitOccurrences.get(key) ?? 0) + row.occurrence_count);
      }
    }
  }
  return {rowByKey, splitOccurrences};
}

/**
 * What the run does with `row`, from its decision and `field`'s
 * create-missing option. An undecided `no_match` value is created when the
 * field creates missing terms; any other undecided or incomplete decision
 * is skipped. A `split` counts the pieces whose own row is undecided.
 */
export function runOutcome(
  row: ImportModuleConceptRow, field: ImportModuleConceptField, index: FieldIndex = indexField(field),
): RunOutcome {
  if (isRowDecided(row)) {
    switch (row.decision_kind) {
      case 'pick_existing':
        return {kind: 'link', candidate: pickedCandidate(row)!};
      case 'create_new':
        return {kind: 'create', parentId: row.new_concept_parent_id ?? null};
      case 'split':
        return splitOutcome(row, field, index);
      default:
        return {kind: 'skip'};
    }
  }
  if (row.match_status === 'no_match' && field.create_missing) {
    return {kind: 'create', parentId: field.create_missing.parent_id ?? null};
  }
  return {kind: 'undecided'};
}

/** How the page shows `outcome`; `parentLabel` names a create-under
 * parent by its id. */
export function outcomeView(outcome: RunOutcome, parentLabel: (id: string) => string): RunOutcomeView {
  switch (outcome.kind) {
    case 'link':
      return linkView(outcome.candidate!);
    case 'create':
      return outcome.parentId
        ? {tone: 'ok', titleKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_CREATE',
           detailKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_CREATE_UNDER',
           detailParams: {path: parentLabel(outcome.parentId)}}
        : {tone: 'ok', titleKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_CREATE',
           detailKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_CREATE_ROOT'};
    case 'split':
      return outcome.undecidedPieces
        ? {tone: 'warn', titleKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_SPLIT',
           titleParams: {count: outcome.pieceCount!},
           detailKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_SPLIT_UNDECIDED',
           detailParams: {count: outcome.undecidedPieces}}
        : {tone: 'ok', titleKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_SPLIT',
           titleParams: {count: outcome.pieceCount!}};
    case 'skip':
      return {tone: 'neutral', titleKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_SKIP'};
    default:
      return {tone: 'warn', titleKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_SKIP',
              detailKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_UNDECIDED'};
  }
}

function linkView(candidate: ImportModuleConceptCandidate): RunOutcomeView {
  const id = shortTermId(candidate.candidate_artifact_id);
  const view: RunOutcomeView = {
    tone: 'ok',
    titleKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_LINK',
    titleParams: {name: candidate.candidate_name},
    detailText: candidate.candidate_has_authority ? `KulturNav · ${id}` : id,
  };
  if (candidate.candidate_valid === false) {
    return {...view, tone: 'warn', detailText: undefined,
            detailKey: 'TRANS__IMPORT__VALUE_REVIEW__OUTCOME_STRUCK', detailParams: {id}};
  }
  return view;
}

function splitOutcome(row: ImportModuleConceptRow, field: ImportModuleConceptField, index: FieldIndex): RunOutcome {
  const pieces = row.pieces ?? [];
  const undecidedPieces = pieces.filter(piece => {
    const pieceRow = index.rowByKey.get(piece.value_key);
    return !!pieceRow && pieceRow !== row && runOutcome(pieceRow, field, index).kind === 'undecided';
  }).length;
  return {kind: 'split', pieceCount: pieces.length, undecidedPieces};
}

/** One piece of a composite value and what the run does with it: its own
 * row's outcome, or `row` and `view` null when the piece names a term by
 * itself. */
export interface PieceView {
  piece: { value: string; value_key: string };
  row: ImportModuleConceptRow | null;
  view: RunOutcomeView | null;
}

export function pieceViews(
  row: ImportModuleConceptRow, field: ImportModuleConceptField, parentLabel: (id: string) => string,
  index: FieldIndex = indexField(field),
): PieceView[] {
  return (row.pieces ?? []).map(piece => {
    const pieceRow = index.rowByKey.get(piece.value_key);
    return pieceRow && pieceRow !== row
      ? {piece, row: pieceRow, view: outcomeView(runOutcome(pieceRow, field, index), parentLabel)}
      : {piece, row: null, view: null};
  });
}

export interface FieldProgress {
  total: number;
  decided: number;
  /** Source cells holding the field's reviewed values. */
  occurrences: number;
  /** Values the run skips for want of a decision. */
  undecided: number;
}

export function fieldProgress(field: ImportModuleConceptField, index: FieldIndex = indexField(field)): FieldProgress {
  return {
    total: field.review_rows.length,
    decided: field.review_rows.filter(isRowDecided).length,
    occurrences: field.review_rows.reduce((sum, row) => sum + row.occurrence_count, 0),
    undecided: field.review_rows.filter(row => runOutcome(row, field, index).kind === 'undecided').length,
  };
}

/** `FieldProgress` summed over several fields; `fields` counts the ones
 * with a value to review. */
export interface ReviewTotals {
  values: number;
  fields: number;
  occurrences: number;
  decided: number;
  undecided: number;
}

export function reviewTotals(progress: FieldProgress[]): ReviewTotals {
  return {
    values: progress.reduce((sum, p) => sum + p.total, 0),
    fields: progress.filter(p => p.total).length,
    occurrences: progress.reduce((sum, p) => sum + p.occurrences, 0),
    decided: progress.reduce((sum, p) => sum + p.decided, 0),
    undecided: progress.reduce((sum, p) => sum + p.undecided, 0),
  };
}

/**
 * How many source cells `row`'s decision covers: its own count, plus -
 * for a value that is also a piece of composites - the counts of the
 * composites decided `split`. A piece's own `occurrence_count` counts
 * only the times it stands alone.
 */
export function occurrencesOf(
  row: ImportModuleConceptRow, field: ImportModuleConceptField, index: FieldIndex = indexField(field),
): number {
  let total = row.occurrence_count + (index.splitOccurrences.get(row.source_value_key) ?? 0);
  if (row.decision_kind === 'split' && row.pieces?.some(piece => piece.value_key === row.source_value_key)) {
    // A composite decided `split` does not add to its own count.
    total -= row.occurrence_count;
  }
  return total;
}

export function matchesFilter(row: ImportModuleConceptRow, filter: ReviewFilter): boolean {
  if (filter === 'all') {
    return true;
  }
  if (filter === 'undecided') {
    return !isRowDecided(row);
  }
  return row.match_status === filter;
}

/** How many of `field`'s rows each filter shows. */
export function filterCounts(field: ImportModuleConceptField): Record<ReviewFilter, number> {
  const counts = {all: 0, undecided: 0, ambiguous: 0, no_match: 0, composite: 0, struck_only: 0};
  for (const row of field.review_rows) {
    counts.all++;
    if (!isRowDecided(row)) {
      counts.undecided++;
    }
    if (row.match_status) {
      counts[row.match_status]++;
    }
  }
  return counts;
}

/** `field`'s rows that pass `filter` and contain `query` (ignoring case),
 * most occurrences first or by value. */
export function visibleRows(
  field: ImportModuleConceptField, filter: ReviewFilter, query: string, sort: ReviewSort,
  index: FieldIndex = indexField(field),
): ImportModuleConceptRow[] {
  const needle = query.trim().toLocaleLowerCase();
  const rows = field.review_rows.filter(row =>
    matchesFilter(row, filter) && (!needle || row.source_value.toLocaleLowerCase().includes(needle)));
  if (sort === 'value') {
    return rows.sort((a, b) => a.source_value.localeCompare(b.source_value));
  }
  const counts = new Map(rows.map(row => [row, occurrencesOf(row, field, index)]));
  return rows.sort((a, b) => counts.get(b)! - counts.get(a)! || a.source_value.localeCompare(b.source_value));
}

/** The candidate most objects use, when it is the only one with that count
 * and it is used at all - a hint for telling same-named terms apart. */
export function mostUsedCandidate(row: ImportModuleConceptRow): ImportModuleConceptCandidate | null {
  const used = row.candidates.filter(c => (c.candidate_usage_count ?? 0) > 0);
  if (row.candidates.length < 2 || !used.length) {
    return null;
  }
  const top = Math.max(...used.map(c => c.candidate_usage_count!));
  const leaders = used.filter(c => c.candidate_usage_count === top);
  return leaders.length === 1 ? leaders[0] : null;
}

/** Names a term of `options` by its full trail ("Godstyp › B › BII"); an
 * id not among them by its short id. */
export function termTrailLabel(options: ConceptOption[]): (id: string) => string {
  const byId = new Map<string, ConceptOption>();
  for (const option of options) {
    if (!byId.has(option.id)) {
      byId.set(option.id, option);
    }
  }
  return id => {
    const option = byId.get(id);
    if (!option) {
      return shortTermId(id);
    }
    const ancestors = conceptOptionAncestors(option);
    const own = conceptOptionLabel(option);
    return ancestors ? `${ancestors} › ${own}` : own;
  };
}

/** The first eight characters of a term id's UUID part - enough to tell
 * candidates apart on screen. */
export function shortTermId(artifactId: string): string {
  const uuid = artifactId.includes('-') ? artifactId.slice(artifactId.indexOf('-') + 1) : artifactId;
  return uuid.slice(0, 8);
}
